import type { ToggleGroupSingleProps } from "@radix-ui/react-toggle-group";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import clsx from "clsx";
import { toggleGroup, toggleItem } from "./ToggleGroup.css";

/** A single item within a ToggleGroup. */
export interface ToggleGroupItemProps {
  /** The value this item represents. */
  value: string;
  /** Label rendered inside the button. */
  label: string;
  /** Whether this item is disabled. */
  disabled?: boolean;
}

/** Props for the ToggleGroup component. */
export interface ToggleGroupProps extends Omit<
  ToggleGroupSingleProps,
  "type" | "value" | "onValueChange" | "onChange"
> {
  /** The items to render as toggle buttons. */
  items: ToggleGroupItemProps[];
  /** The currently selected value. */
  value: string;
  /** Called when the user selects a different item. */
  onChange: (value: string) => void;
  /** Whether the entire group is disabled. */
  disabled?: boolean;
}

/** Single-select segmented control. Wraps Radix ToggleGroup for accessibility. */
export function ToggleGroup({
  items,
  value,
  onChange,
  disabled = false,
  className,
  ...rest
}: ToggleGroupProps) {
  function handleValueChange(next: string) {
    // Radix fires "" when the user clicks the already-selected item — ignore it
    // to keep the selection required (at least one item always selected).
    if (next) onChange(next);
  }

  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      onValueChange={handleValueChange}
      disabled={disabled}
      className={clsx(toggleGroup, className)}
      {...rest}
    >
      {items.map((item) => (
        <ToggleGroupPrimitive.Item
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          className={toggleItem}
        >
          {item.label}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}
